import { useEffect, useState, useRef, useCallback } from "react";
import {
  MdEdit,
  MdClose,
  MdUpload,
  MdAdd,
  MdDelete,
  MdViewCarousel,
} from "react-icons/md";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { adminApiService } from "services/api";

Modal.setAppElement("#root");

const CardPreview = ({
  title,
  imageUrl,
  link,
  onEdit,
  onDelete,
  className = "",
}) => (
  <div
    className={`relative overflow-hidden rounded-xl border border-gray-200 shadow-sm transition-all hover:shadow-md ${className}`}
  >
    <img src={imageUrl} alt={title} className="h-48 w-full object-cover" />
    <div className="bg-black absolute inset-0 flex items-center justify-center gap-2 bg-opacity-0 opacity-0 transition-all hover:bg-opacity-30 hover:opacity-100">
      <button
        onClick={onEdit}
        className="rounded-full bg-white p-2 text-blue-600 shadow-md hover:bg-blue-50"
        aria-label={`Edit ${title}`}
      >
        <MdEdit size={20} />
      </button>
      <button
        onClick={onDelete}
        className="rounded-full bg-white p-2 text-red-600 shadow-md hover:bg-red-50"
        aria-label={`Delete ${title}`}
      >
        <MdDelete size={20} />
      </button>
    </div>
    <div className="p-4">
      <p className="truncate font-medium text-gray-800">{title}</p>
      <p className="truncate text-sm text-gray-500">{link}</p>
    </div>
  </div>
);

const FileUploadArea = ({
  previewUrl,
  onFileChange,
  inputRef,
  accept = "image/*",
  label = "Upload image",
}) => (
  <div className="mt-1 flex items-center">
    <label
      htmlFor="fileInput"
      className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-10 hover:bg-gray-100"
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="Preview"
          className="mb-2 h-40 w-full object-cover"
        />
      ) : (
        <div className="flex flex-col items-center justify-center text-center">
          <MdUpload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-1 text-sm text-gray-600">
            <span className="font-medium text-blue-600">Click to upload</span>{" "}
            or drag and drop
          </p>
          <p className="text-xs text-gray-500">JPG, PNG up to 10MB</p>
        </div>
      )}
      <input
        id="fileInput"
        type="file"
        accept={accept}
        onChange={onFileChange}
        ref={inputRef}
        className="hidden"
      />
    </label>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="border-t-transparent h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-opacity-50"></div>
  </div>
);

export default function CategorySlider() {
  const [cards, setCards] = useState([]);
  const [modalState, setModalState] = useState({
    isOpen: false,
    previewUrl: "",
    isNew: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    button_link: "",
    image: null,
  });

  const fileInputRef = useRef(null);

  const NoCardsState = () => (
    <div className="flex h-64 flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
      <MdViewCarousel className="mb-4 h-16 w-16 text-gray-400" />
      <h3 className="mb-2 text-lg font-medium text-gray-900">
        No Category Cards
      </h3>
      <p className="mb-4 text-sm text-gray-500">
        Get started by adding your first category card to the slider.
      </p>
      <button
        onClick={() => openModal()}
        className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <MdAdd className="mr-2" size={20} />
        Add New Card
      </button>
    </div>
  );

  const fetchCards = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getCategorySliders();
      if (!res.ok) throw new Error("Failed to fetch cards");
      const { data } = await res.json();
      setCards(data);
    } catch (error) {
      toast.error("Error fetching category cards.");
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const openModal = (card = null) => {
    if (card) {
      setActiveCard(card);
      setFormData({
        title: card.title,
        button_link: card.button_link,
        image: null,
      });
      setModalState({
        isOpen: true,
        previewUrl: `${process.env.REACT_APP_API_URL_Plain}/config/uploads/category_slider/${card.image_name}`,
        isNew: false,
      });
    } else {
      setActiveCard(null);
      setFormData({
        title: "",
        button_link: "",
        image: null,
      });
      setModalState({
        isOpen: true,
        previewUrl: "",
        isNew: true,
      });
    }
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      previewUrl: "",
      isNew: false,
    });
    setActiveCard(null);
    setFormData({
      title: "",
      button_link: "",
      image: null,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, image: file }));
      setModalState((prev) => ({
        ...prev,
        previewUrl: URL.createObjectURL(file),
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.button_link) {
      return toast.error("Please fill in all required fields.");
    }

    setIsLoading(true);
    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("link", formData.button_link);
      if (formData.image) {
        submitData.append("category_slider_image", formData.image);
      }
      if (activeCard) {
        submitData.append("id", activeCard.id);
      }

      const res = modalState.isNew ? await adminApiService.addCategorySlider(submitData)
        : await adminApiService.updateCategorySlider(submitData);



      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to save card");
      }

      toast.success(
        `Category card ${modalState.isNew ? "added" : "updated"} successfully!`
      );
      closeModal();
      fetchCards();
    } catch (error) {
      toast.error(
        `Error ${modalState.isNew ? "adding" : "updating"} category card: ${
          error.message
        }`
      );
      console.error("Submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm(`Are you sure you want to delete "${card.title}"?`)) {
      return;
    }

    setIsLoading(true);
    try {
      const res = await adminApiService.deleteCategorySlider(card.id);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to delete card");
      }

      toast.success("Category card deleted successfully!");
      fetchCards();
    } catch (error) {
      toast.error(`Error deleting category card: ${error.message}`);
      console.error("Delete error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl p-6">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Category Slider Management
        </h1>
        <p className="mt-2 text-gray-600">
          Manage the category cards displayed in the slider
        </p>
      </header>

      <div className="mb-6 flex justify-end">
        <button
          onClick={() => openModal()}
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <MdAdd className="mr-2" size={20} />
          Add New Card
        </button>
      </div>

      {isLoading && !cards.length ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : cards.length === 0 ? (
        <NoCardsState />
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {cards.map((card) => (
            <CardPreview
              key={card.id}
              title={card.title}
              imageUrl={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/category_slider/${card.image_name}`}
              link={card.button_link}
              onEdit={() => openModal(card)}
              onDelete={() => handleDelete(card)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={modalState.isOpen}
        onRequestClose={closeModal}
        contentLabel={
          modalState.isNew ? "Add Category Card" : "Edit Category Card"
        }
        className="relative mx-auto max-w-lg rounded-xl bg-white p-6 shadow-2xl outline-none"
        overlayClassName="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
        closeTimeoutMS={200}
      >
        <button
          onClick={closeModal}
          className="absolute right-4 top-4 rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Close modal"
        >
          <MdClose size={24} />
        </button>

        <h2 className="mb-6 text-2xl font-semibold text-gray-800">
          {modalState.isNew ? "Add New Category Card" : "Edit Category Card"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="title"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label
              htmlFor="button_link"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Link *
            </label>
            <input
              type="text"
              id="button_link"
              name="button_link"
              value={formData.button_link}
              onChange={handleInputChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Category Image {modalState.isNew ? "*" : "(optional)"}
            </label>
            <FileUploadArea
              previewUrl={modalState.previewUrl}
              onFileChange={handleFileChange}
              inputRef={fileInputRef}
              accept="image/*"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-70"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner /> Saving...
                </>
              ) : modalState.isNew ? (
                "Add Card"
              ) : (
                "Update Card"
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
